import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithReset } from 'jotai/utils';

import { materialsAtom } from '@/_atoms/batch';

import type { Material } from '@/types/batch';
import type { ScoringRunState, ScoringRunStatus } from '@/types/scoringRun';

/** Set of ids for scoring runs currently being polled. */
export const pollingScoringRunIdsAtom = atomWithReset<Set<string>>(new Set<string>());

/** Atom recording the scoring run status for each survey. */
export const scoringRunAtomFamily = atomFamily((_surveyId: string) =>
  atom<ScoringRunState | null>(null)
);

/**
 * Returns the scoring status across all materials.
 *
 * - Returns the status of the first non-successful scoring run (if any).
 * - Returns 'S' if all scoring runs are successful.
 * - Returns null if there are no scoring runs.
 */
export const overallScoringStatusAtom = atom<ScoringRunStatus | null>((get) => {
  const materials: Material[] = get(materialsAtom);

  let overallStatus: ScoringRunStatus | null = null;

  for (const { survey } of materials) {
    const state: ScoringRunState | null = get(scoringRunAtomFamily(survey));
    if (!state) continue;

    if (state.status !== 'S') return state.status;

    overallStatus = 'S';
  }

  return overallStatus;
});

/** Atom storing the last error encountered when attempting to poll a scoring run's status. */
export const scoringRunErrorAtom = atomWithReset<Error | null>(null);